import { useCallback, useRef, useState } from 'react';
import {
  getLocationAutocomplete,
  mergeLocationResults,
  searchLocationByName,
  type LocationAutocompleteParams,
} from '@/api/location';
import Location from '@/models/location';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_LOCATION_AUTOCOMPLETE_EMPTY,
  EVENT_LOCATION_AUTOCOMPLETE_FAILED,
  EVENT_LOCATION_AUTOCOMPLETE_REQUEST,
  EVENT_LOCATION_AUTOCOMPLETE_SUCCESS,
} from '@/lib/analyticsEvents';
import { error as logError } from '@/utils/logger';

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_MS = 500;
const FETCH_TIMEOUT_MS = 15000;

/**
 * Place search for Create New Link Explore tab.
 * Mirrors PlacePicker search: debounce before fetch, loading only while a request
 * is in flight, keep prior results until new ones arrive (avoids modal collapse).
 */
export function useCreateLinkPlaceSearch() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Location[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  /** True after the user has typed enough to trigger a search attempt. */
  const [searchAttempted, setSearchAttempted] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  const clearPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    // The location APIs don't take the abort signal, so also drop late responses.
    requestIdRef.current += 1;
  }, []);

  const resetSearch = useCallback(() => {
    clearPending();
    setQuery('');
    setResults([]);
    setIsSearching(false);
    setSearchError(null);
    setSearchAttempted(false);
  }, [clearPending]);

  const runSearch = useCallback(async (input: string) => {
    const trimmed = input.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) {
      setResults([]);
      setIsSearching(false);
      setSearchError(null);
      return;
    }

    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    abortControllerRef.current = new AbortController();
    const requestId = ++requestIdRef.current;

    setIsSearching(true);
    setSearchError(null);
    setSearchAttempted(true);

    const params: LocationAutocompleteParams = {
      input: trimmed,
      filterMode: 'all',
    };

    timeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (requestId !== requestIdRef.current) return;
      setIsSearching(false);
      setSearchError('Request timed out. Please try again.');
    }, FETCH_TIMEOUT_MS);

    WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_REQUEST, {
      searchText: trimmed,
      surface: 'create_link_flow',
    });

    try {
      const [autocompleteResult, textSearchResult] = await Promise.allSettled([
        getLocationAutocomplete(params),
        searchLocationByName({ locationName: trimmed }),
      ]);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (requestId !== requestIdRef.current) return;

      const autocompleteHits =
        autocompleteResult.status === 'fulfilled' ? autocompleteResult.value : [];
      const textSearchHits =
        textSearchResult.status === 'fulfilled' ? textSearchResult.value : [];

      const merged = mergeLocationResults(autocompleteHits, textSearchHits);
      setResults(merged);
      setIsSearching(false);

      if (merged.length > 0) {
        WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_SUCCESS, {
          searchText: trimmed,
          resultCount: merged.length,
          surface: 'create_link_flow',
        });
      } else {
        WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_EMPTY, {
          searchText: trimmed,
          surface: 'create_link_flow',
        });
      }
    } catch (err) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (err instanceof Error && err.name === 'AbortError') return;
      if (requestId !== requestIdRef.current) return;

      logError('[useCreateLinkPlaceSearch] search failed', err);
      WebAnalytics.trackSearch(EVENT_LOCATION_AUTOCOMPLETE_FAILED, {
        searchText: trimmed,
        surface: 'create_link_flow',
        errorMessage: err instanceof Error ? err.message : 'Unknown',
      });
      setSearchError('Search failed. Please try again.');
      setIsSearching(false);
    }
  }, []);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      // Every keystroke invalidates the pending debounce and any in-flight request,
      // so a stale response can't land after the query changed.
      clearPending();

      const trimmed = value.trim();
      if (trimmed.length < MIN_SEARCH_LENGTH) {
        // Too short to search — back to Popular right away.
        setSearchAttempted(false);
        setResults([]);
        setSearchError(null);
        setIsSearching(false);
        return;
      }

      // Show the content loader during the debounce window too, so results
      // area never looks idle/blank between keystroke and request.
      setIsSearching(true);
      setSearchError(null);
      setSearchAttempted(true);

      debounceRef.current = setTimeout(() => {
        void runSearch(trimmed);
      }, DEBOUNCE_MS);
    },
    [clearPending, runSearch],
  );

  return {
    query,
    handleQueryChange,
    results,
    isSearching,
    searchError,
    searchAttempted,
    resetSearch,
    retrySearch: () => {
      if (query.trim().length >= MIN_SEARCH_LENGTH) {
        void runSearch(query.trim());
      }
    },
  };
}
